// ===========================================================================
// HighCostFilters — barra compacta de filtros del Tab Pacientes.
//
// El estado de los filtros lo gobierna el padre (PatientsTab, sincronizado con
// la URL). Aquí solo renderizamos los controles y notificamos cambios.
//
// Nota: el filtro de PCP envía `pcpName` (no pcpEmployeeId) — el catálogo de
// PCPs expone employeePcpId, que no necesariamente coincide con el pcpEmployeeId
// que filtra High Cost. Ver OBSERVACIONES_FRONTEND_HIGH_COST.md.
// ===========================================================================
import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";

export default function HighCostFilters({ filters, onChange, onClear }) {
  const [locations, setLocations] = useState([]);
  const [pcps, setPcps] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [locs, pcpList] = await Promise.all([
          getLocations({ status: 1 }),
          getPcps({ status: 1 }),
        ]);
        if (!active) return;
        setLocations(Array.isArray(locs) ? locs : []);
        setPcps(Array.isArray(pcpList) ? pcpList : []);
      } catch {
        /* dropdowns opcionales: si fallan, quedan vacíos */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const set = (key) => (e) => onChange({ [key]: e.target.value });

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search name or CIP"
          value={filters.search || ""}
          onChange={set("search")}
          sx={{ minWidth: 240, flex: "1 1 240px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          size="small"
          type="number"
          label="Cost ≥"
          value={filters.minTotalCost || ""}
          onChange={set("minTotalCost")}
          sx={{ width: 130 }}
        />

        <TextField
          size="small"
          select
          label="Min GI"
          value={filters.minGlobalIndicator || ""}
          onChange={set("minGlobalIndicator")}
          sx={{ width: 110 }}
        >
          <MenuItem value="">Any</MenuItem>
          {[3, 4, 5, 6, 7].map((n) => (
            <MenuItem key={n} value={n}>
              {n}+
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Condition"
          placeholder="e.g. Diabetes"
          value={filters.condition || ""}
          onChange={set("condition")}
          sx={{ width: 160 }}
        />

        <TextField
          size="small"
          select
          label="Gender"
          value={filters.gender || ""}
          onChange={set("gender")}
          sx={{ width: 110 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="M">M</MenuItem>
          <MenuItem value="F">F</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Location"
          value={filters.locationId || ""}
          onChange={set("locationId")}
          sx={{ width: 200 }}
        >
          <MenuItem value="">All locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.locationId} value={loc.locationId}>
              {loc.medicalCenter}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          select
          label="PCP"
          value={filters.pcpName || ""}
          onChange={set("pcpName")}
          sx={{ width: 200 }}
        >
          <MenuItem value="">All PCPs</MenuItem>
          {pcps.map((p) => (
            <MenuItem key={p.employeePcpId} value={p.pcpFullName}>
              {p.pcpFullName}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filters.onlyActive !== false}
              onChange={(e) => onChange({ onlyActive: e.target.checked })}
            />
          }
          label="Only active"
          sx={{ ml: 0 }}
        />

        <Button size="small" startIcon={<ClearIcon />} onClick={onClear} sx={{ textTransform: "none" }}>
          Clear
        </Button>
      </Stack>
    </Box>
  );
}
